import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiFetch } from '../apiClient';
import { ErrorBanner } from './Configuration';
import { selectableProjects, projectOptionLabel } from './ProjectRegisters';
import { MultiSelect, FilterCheckbox } from './registerFilters';
import { displayDate } from './registerFormat';
import { useSelectedProjects, usePruneMissingProjects } from './projectDeepLink';
import ProjectEventModal from './ProjectEventForm';

const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales: { 'en-US': enUS },
});

// apps/web cannot import packages/shared (ADR 0019) -- mirrors CALENDAR_EVENT_TYPES there, plus the
// label/color each type gets on this page only (the API doesn't know about color).
export const EVENT_TYPES = [
  { value: 'milestone', label: 'Milestones', color: 'var(--primary-dark)' },
  { value: 'phase', label: 'Phases', color: 'var(--primary-light)' },
  { value: 'task', label: 'Tasks', color: 'var(--teal)' },
  { value: 'bpa', label: 'BPA Sessions', color: 'var(--violet)' },
  { value: 'raaidd', label: 'RAAIDD (Risk/Action)', color: 'var(--yellow)' },
  { value: 'change', label: 'Change Register', color: 'var(--green)' },
  { value: 'bau', label: 'Billed-as-Used', color: 'var(--gray)' },
  { value: 'event', label: 'Project Events', color: 'var(--orange, #d9730d)' },
];
const COLOR_BY_TYPE = Object.fromEntries(EVENT_TYPES.map((t) => [t.value, t.color]));
// Yellow is light -- every other event color is dark enough for white text.
const DARK_TEXT_TYPES = new Set(['raaidd']);

// A second, independent color channel from the event-type background above: with several projects
// on screen at once (internal view only -- the customer portal only ever shows its own project),
// a small dot in front of the title is the "which project is this" answer the background color
// already spent on "what kind of event is this." Deliberately not any of the seven colors above.
const PROJECT_DOT_COLORS = [
  '#d6336c', '#3b5bdb', '#5c940d', '#e8590c', '#862e9c', '#0b7285', '#495057', '#66a80f',
];

function projectColorFor(projectId) {
  let hash = 0;
  for (let i = 0; i < projectId.length; i += 1) hash = (hash * 31 + projectId.charCodeAt(i)) | 0;
  return PROJECT_DOT_COLORS[Math.abs(hash) % PROJECT_DOT_COLORS.length];
}

// Only the time-of-day parts of these are read by react-big-calendar's `min`/`max` -- narrows the
// week view to business hours instead of a full midnight-to-midnight column.
const WORKING_HOURS_MIN = new Date(1972, 0, 1, 7, 0, 0);
const WORKING_HOURS_MAX = new Date(1972, 0, 1, 19, 0, 0);

/**
 * Turn the API's rows into what react-big-calendar wants.
 *
 * `new Date` is right for both wire formats the API sends (see `CalendarEvent.start` in
 * calendar.service.ts): an instant with a `Z` becomes that moment in the viewer's own zone, and a
 * floating `2026-08-14T09:30:00` from the portal becomes 9:30 wherever it's read — which is the
 * point of sending it that way.
 */
export function toCalendarEvents(rows) {
  return rows.map((e) => ({ ...e, start: new Date(e.start), end: new Date(e.end) }));
}

/**
 * The calendar itself — legend, grid, and the event detail card.
 *
 * Shared with the portal's `CustomerProjectCalendar.js` for the same reason the four registers
 * share their grids with their customer twins: the event colors, the month/week pair, the working
 * hours and the detail card must not drift between the two views. What each page keeps is only its
 * own scoping and filters, which is where they genuinely differ.
 *
 * `linkFor(event)` returns where the detail card's "View" button goes, or null for no button — the
 * internal routes and the portal's are different sets, and neither page should know the other's.
 */
export function CalendarSurface({ events, legendExtra = null, linkFor }) {
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  const eventPropGetter = useCallback((event) => ({
    style: {
      backgroundColor: COLOR_BY_TYPE[event.type] || 'var(--gray)',
      color: DARK_TEXT_TYPES.has(event.type) ? 'var(--text-dark)' : 'var(--white)',
      border: event.isGoLive ? '2px solid var(--red)' : 'none',
      borderRadius: 4,
      fontSize: 12,
    },
  }), []);

  const eventContent = useCallback(({ event }) => (
    <span>
      {event._dot && (
        <span
          style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
            background: event._dot, marginRight: 5,
          }}
          aria-hidden
        />
      )}
      {event.title}
    </span>
  ), []);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
        {EVENT_TYPES.map((t) => (
          <span
            key={t.value}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)' }}
          >
            <span
              style={{ width: 10, height: 10, borderRadius: 3, background: t.color, display: 'inline-block' }}
              aria-hidden
            />
            {t.label}
          </span>
        ))}
      </div>
      {legendExtra}
      <div style={{ height: 720 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={[Views.MONTH, Views.WEEK]}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          min={WORKING_HOURS_MIN}
          max={WORKING_HOURS_MAX}
          popup
          eventPropGetter={eventPropGetter}
          components={{ event: eventContent }}
          onSelectEvent={setSelectedEvent}
        />
      </div>
      {selectedEvent && (
        <EventDetail
          event={selectedEvent}
          linkFor={linkFor}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </div>
  );
}

export default function ProjectCalendar() {
  // Restores whichever projects were last selected on the calendar (ADR 0042), or a `?project=<id>`
  // deep link (ADR 0038).
  const [selectedProjectIds, setSelectedProjectIds] = useSelectedProjects();
  const [projects, setProjects] = useState([]);
  const [showArchived, setShowArchived] = useState(!!selectedProjectIds.length);
  // Empty means "every type" -- same MultiSelect convention as the Project Registers filters.
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiFetch('/projects');
        if (!cancelled) setProjects(rows);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleProjects = useMemo(
    () => selectableProjects(projects, showArchived),
    [projects, showArchived],
  );

  const projectOptions = useMemo(
    () => visibleProjects.map((p) => ({ value: p.id, label: projectOptionLabel(p, null) })),
    [visibleProjects],
  );

  // Dropping a project from the visible list (unticking "show archived" while it's selected)
  // shouldn't leave a phantom id in the filter forever — but not before the list has loaded, or a
  // deep link gets cleared on the way in (ADR 0038).
  usePruneMissingProjects(
    selectedProjectIds, setSelectedProjectIds, visibleProjects, projects.length > 0,
  );

  const loadEvents = useCallback(async () => {
    if (!selectedProjectIds.length) {
      setEvents([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ projectIds: selectedProjectIds.join(',') });
      if (selectedTypes.length) params.set('types', selectedTypes.join(','));
      const rows = await apiFetch(`/calendar/events?${params.toString()}`);
      setEvents(toCalendarEvents(rows));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProjectIds, selectedTypes]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // The project dot only earns its place once there's more than one project to tell apart.
  const showProjectDots = selectedProjectIds.length > 1;
  const renderedEvents = useMemo(
    () => (showProjectDots ? events.map((e) => ({ ...e, _dot: projectColorFor(e.projectId) })) : events),
    [events, showProjectDots],
  );

  const projectLegend = showProjectDots ? (
    <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
      {visibleProjects
        .filter((p) => selectedProjectIds.includes(p.id))
        .map((p) => (
          <span
            key={p.id}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)' }}
          >
            <span
              style={{
                width: 7, height: 7, borderRadius: '50%',
                background: projectColorFor(p.id), display: 'inline-block',
              }}
              aria-hidden
            />
            {p.name}
          </span>
        ))}
    </div>
  ) : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Scheduling</div>
        <h1 className="page-title">Project Calendar</h1>
        <p className="page-subtitle">
          Every scheduled activity across a project's milestones, phases, tasks, BPA sessions and
          registers, in one view.
        </p>
      </div>

      <ErrorBanner error={error} />

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <MultiSelect
            label="Projects"
            options={projectOptions}
            selected={selectedProjectIds}
            onChange={setSelectedProjectIds}
            allLabel="— Select project(s) —"
            minWidth={280}
            showSelectAll
          />
          <MultiSelect
            label="Event types"
            options={EVENT_TYPES}
            selected={selectedTypes}
            onChange={setSelectedTypes}
            allLabel="All types"
            minWidth={220}
            showSelectAll
          />
          <FilterCheckbox label="Show archived" checked={showArchived} onChange={setShowArchived} />
          {/* Only once there's a project to log the event against. */}
          {!!selectedProjectIds.length && (
            <button className="btn btn-outline" onClick={() => setAddingEvent(true)}>+ Add Project Event</button>
          )}
        </div>
      </div>

      {addingEvent && (
        <ProjectEventModal
          projects={visibleProjects.filter((p) => selectedProjectIds.includes(p.id))}
          initialProjectId={selectedProjectIds[0]}
          onClose={() => setAddingEvent(false)}
          onSaved={loadEvents}
        />
      )}

      {!selectedProjectIds.length ? (
        <div className="placeholder-page">
          <div className="placeholder-title">No project selected</div>
          <div className="placeholder-sub">Select one or more projects above to see their calendar.</div>
        </div>
      ) : loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-mid)' }}>
          Loading calendar…
        </div>
      ) : (
        <CalendarSurface
          events={renderedEvents}
          legendExtra={projectLegend}
          linkFor={(event) => event.link}
        />
      )}
    </div>
  );
}

function EventDetail({ event, linkFor, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const typeLabel = EVENT_TYPES.find((t) => t.value === event.type)?.label ?? event.type;
  const sameDay = !event.end || event.end.getTime() === event.start.getTime();
  const link = linkFor ? linkFor(event) : null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.35)' }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-event-title"
        className="card"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 41, width: 420, maxWidth: 'calc(100vw - 24px)', marginBottom: 0,
          boxShadow: '0 10px 34px rgba(0,0,0,0.28)',
        }}
      >
        <div id="calendar-event-title" className="card-title">{event.title}</div>
        <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 0 }}>
          {typeLabel} · {event.projectName}
        </p>
        <p style={{ fontSize: 13 }}>
          {displayDate(event.start)}
          {sameDay ? '' : ` – ${displayDate(event.end)}`}
          {event.timeLabel ? ` · ${event.timeLabel}` : ''}
        </p>
        {event.detail && <p style={{ fontSize: 13, color: 'var(--text-mid)' }}>{event.detail}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" onClick={onClose}>Close</button>
          {link && (
            <button className="btn btn-primary" onClick={() => navigate(link)}>View</button>
          )}
        </div>
      </div>
    </>
  );
}
